import { NavLink } from "react-router-dom";
import "../style/testing_types.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

export default function TestingTypes  () {
  return (
    <>
      {/* Header Banner */}
      <div className="testing-types-header-banner">
        <div className="icon-circle">
          <i className="fa-solid fa-box"></i>
        </div>
        <h1>Software Testing: Types Explained</h1>
        <p>
          A comprehensive breakdown of every major testing category. Learn when
          to use Functional, Non-functional, Manual, and Automated testing to
          achieve complete application coverage.
        </p>

        <div className="header-stats">
          <div className="header-stat">
            15+ <span>Functional Testing Types</span>
          </div>
          <div className="header-stat">
            20+ <span>Non-Functional Testing Types</span>
          </div>
          <div className="header-stat">
            50+ <span>Key Terms Defined</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="nav-tabs">
        <NavLink to="/software-testing">
          <i className="fa-solid fa-book-open"></i> Overview
        </NavLink>
        <NavLink to="/testing-types" className="active">
          <i className="fa-solid fa-box"></i> Types
        </NavLink>
        <NavLink to="/testing-pyramid">
          <i className="fa-solid fa-puzzle-piece"></i> Pyramid
        </NavLink>
        <NavLink to="/testing-methods">
          <i className="fa-solid fa-code"></i> Methods
        </NavLink>
        <NavLink to="/testing-practices">
          <i className="fa-solid fa-lightbulb"></i> Practices
        </NavLink>
        <NavLink to="/testing-tools">
          <i className="fa-solid fa-screwdriver-wrench"></i> Tools
        </NavLink>
      </nav>

      {/* Content */}
      <section className="testing-types-section" style={{ paddingTop: "40px" }}>
        <h2>Types of Software Testing</h2>
        <p className="section-subtitle">
          Comprehensive overview of different testing approaches and when to use them
        </p>

            <div className="type-cards-grid">

            <div className="type-card foundation-card">
                <div className="card-header"><i className="fa-solid fa-box-archive"></i><span
                        className="tag foundation-tag">Foundation</span></div>
              <h3>
  Unit Testing{" "}
  <NavLink to="/unit-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink>
</h3>
                <p>Testing individual components or functions in isolation</p>
                <h4>Key Aspects:
                </h4>
                <ul>
                    <li>Smallest testable parts of code</li>
                    <li>Fast execution and quick feedback</li>
                    <li>Usually automated and run frequently</li>
                    <li>Tests one thing at a time</li>
                    <li>Mocks external dependencies</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong> <span>Jest</span><span>JUnit</span><span>PyTest</span><span>NUnit</span></strong></div>
            </div>

            <div className="type-card middle-card">
                <div className="card-header"><i className="fa-solid fa-bezier-curve"></i><span
                        className="tag middle-tag">Middle</span></div>
                <h3>Integration Testing
                   <NavLink to="/integration-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink>
                </h3>
                <p>Testing how different modules or services work together</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Verifies component interactions</li>
                    <li>Tests data flow between modules</li>
                    <li>Identifies interface defects</li>
                    <li>Can test API integrations</li>
                    <li>Validates system cohesion</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>Postman</span><span>SoapUI</span><span>REST Assured</span><span>Pact</span>
                </strong></div>
            </div>

            <div className="type-card top-card">
                <div className="card-header"><i className="fa-solid fa-globe"></i><span className="tag top-tag">Top</span></div>
                <h3>End-to-End Testing</h3>
                <p>Testing complete user workflows from start to finish</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Simulates real user scenarios</li>
                    <li>Tests entire application stack</li>
                    <li>Validates business processes</li>
                    <li>Slower but comprehensive</li>
                    <li>Often uses real environments</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags">
                   <strong><span>Cypress</span><span>Playwright</span><span>Selenium</span><span>TestCafe</span></strong></div>
         </div>

            <div className="type-card non-functional-card">
                <div className="card-header"><i className="fa-solid fa-gauge-high"></i><span
                        className="tag non-functional-tag">Non-Functional</span></div>
                <h3>Performance Testing
                  <NavLink to="/what-is-performance-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink>
                </h3>
                <p>Testing speed, responsiveness, and stability under load</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Load testing - expected traffic</li>
                    <li>Stress testing - extreme conditions</li>
                    <li>Spike testing - sudden traffic surges</li>
                    <li>Endurance testing - sustained load</li>
                    <li>Identifies bottlenecks</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>JMeter</span><span>k6</span><span>Gatling</span><span>LoadRunner</span>
                </strong></div>
            </div>

            <div className="type-card non-functional-card">
                <div className="card-header"><i className="fa-solid fa-lock"></i><span
                        className="tag non-functional-tag">Non-Functional</span></div>
                <h3>Security Testing</h3>
                <p>Testing for vulnerabilities and security flaws</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Penetration testing</li>
                    <li>Vulnerability scanning</li>
                    <li>Security audits</li>
                    <li>Authentication/authorization checks</li>
                    <li>Data encryption verification</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>OWASP ZAP</span><span>Burp
                        Suite</span><span>Metasploit</span><span>Nessus</span></strong></div>
            </div>

            <div className="type-card platform-card">
                <div className="card-header"><i className="fa-solid fa-mobile-screen-button"></i><span
                        className="tag platform-tag">Platform</span></div>
                <h3>Mobile Testing</h3>
                <p>Testing mobile applications on various devices and OS versions</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Device compatibility testing</li>
                    <li>Touch gesture validation</li>
                    <li>Battery and memory usage</li>
                    <li>Network condition testing</li>
                    <li>App store compliance</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>Appium</span><span>Detox</span><span>Espresso</span><span>XCUITest</span></strong>
                </div>
            </div>

            <div className="type-card specialized-card-blue">
                <div className="card-header"><i className="fa-solid fa-eye"></i><span
                        className="tag specialized-tag">Specialized</span></div>
                <h3>Visual Regression Testing</h3>
                <p>Detecting unintended visual changes in UI</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Screenshot comparison</li>
                    <li>Pixel-by-pixel analysis</li>
                    <li>Cross-browser consistency</li>
                    <li>Catches CSS/layout bugs</li>
                    <li>Automated visual validation</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags">
                   <strong> <span>Percy</span><span>Chromatic</span><span>BackstopJS</span><span>Applitools</span></strong></div>
            </div>

            <div className="type-card specialized-card-green">
                <div className="card-header"><i className="fa-solid fa-database"></i><span
                        className="tag specialized-tag">Specialized</span></div>
                <h3>Database Testing</h3>
                <p>Validating data integrity, queries, and transactions</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Schema validation</li>
                    <li>Data integrity checks</li>
                    <li>Query performance testing</li>
                    <li>Transaction rollback testing</li>
                    <li>Migration verification</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>DbUnit</span><span>SQLTest</span><span>Liquidbase</span><span>Flyway</span></strong>
                </div>
            </div>

            <div className="type-card maintenance-card">
                <div className="card-header"><i className="fa-solid fa-arrows-rotate"></i><span
                        className="tag maintenance-tag">Maintenance</span></div>
                <h3>Regression Testing<NavLink to="/regression-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink></h3>
                <p>Ensuring new changes don't break existing functionality</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Re-runs existing test suites</li>
                    <li>Verifies fixes don't cause issues</li>
                    <li>Critical for continuous integration</li>
                    <li>Can be fully automated</li>
                    <li>Protects against regressions</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>Automated test suites</span><span>CI/CD pipelines</span></strong></div>
            </div>

            <div className="type-card preliminary-card">
                <div className="card-header"><i className="fa-solid fa-fire"></i><span
                        className="tag preliminary-tag">Preliminary</span></div>
                <h3>Smoke Testing <NavLink to="/what-is-smoke-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink></h3>
                <p>Quick checks to verify basic functionality</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Shallow and wide coverage</li>
                    <li>Quick build verification</li>
                    <li>Determines if we can proceed</li>
                    <li>Critical path validation</li>
                    <li>First line of defense</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>Build verification tests</span><span>Sanity checks</span></strong></div>
            </div>

            <div className="type-card validation-card">
                <div className="card-header"><i className="fa-solid fa-handshake"></i><span
                        className="tag validation-tag">Validation</span></div>
                <h3>Acceptance Testing
                  <NavLink to="/acceptance-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink>
                </h3>
                <p>Validating that system meets business requirements</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>User acceptance testing (UAT)</li>
                    <li>Business requirement validation</li>
                    <li>Real-world scenario testing</li>
                    <li>Customer/stakeholder approval</li>
                    <li>Final verification stage</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>Manual UAT</span><span>Cucumber</span><span>SpecFlow</span></strong></div>
            </div>

            <div className="type-card integration-card-api">
                <div className="card-header"><i className="fa-solid fa-cloud"></i><span
                        className="tag integration-tag">Integration</span></div>
                <h3>
                  API Testing
                   <NavLink to="/api-testing" className="external-link">
    <i className="fas fa-external-link-alt"></i>
  </NavLink></h3>
                <p>Testing application programming interfaces</p>
                <h4>Key Aspects:</h4>
                <ul>
                    <li>Request/response validation</li>
                    <li>Status code verification</li>
                    <li>Data format checking (JSON/XML)</li>
                    <li>Authentication testing</li>
                    <li>Rate limiting and throttling</li>
                </ul>
                <h4>Popular Tools:</h4>
                <div className="tool-tags"><strong><span>Postman</span><span>Insomnia </span><span>REST-Assured </span><span>Paw </span></strong></div>
            </div>

        </div>
      </section>



    </>
  );
};


